import {
  addSetRow,
  addSlot,
  alignGroupSetRows,
  removeSetRow,
  removeSlot,
  setRowKind,
  setSlotGroup,
  isLoadBearingKind,
  setWorkKind,
  updateCell,
  updateSetRowNote,
  updateSlotInstructions,
  updateSlot,
  type CellDraft,
  type Draft,
  type IntensityKind,
} from "@jackedlogs/shared";

import type { LoggerAction } from "../../Logger/state/loggerDraftTypes";

/** PE-22 K2.2 — the draft-direct dispatch adapter (the perno).
 *
 *  The logger card (TimelineFocusSection) fires `LoggerAction`s that in a live
 *  session mutate a `LoggerDraft` through the logger reducer. In `prescribe`
 *  the SAME card must instead write the program `Draft` — so this module
 *  translates each card action into the shared, pure `programEditActions`
 *  (updateCell / addSetRow / removeSetRow / setRowKind / setSlotGroup /
 *  removeSlot), which the mount applies via the composer's own `setDraft`.
 *
 *  ⭐ ONE save-path: the composer's `Draft` (persisted by its own commit/save).
 *  The card never gets a second reducer, and it never writes a `DraftSet` —
 *  reperto 6: `rirPrescribed`/`rpe`/`rir` on a DraftSet are a display-only
 *  MIRROR the program payload does not carry; the real value must land on the
 *  SlotDraft cell's `intensityKind`/`intensityValue`.
 *
 *  ⛔ SCOPE (agreed 2026-08-27, extended by PE-30 and PE-31): K2 owns reps ·
 *  effort · ±set · split-group · remove-exercise, since PE-30 the picker's
 *  commit too — add-exercise · set-group · align-group-sets — and since PE-31
 *  the swap picker's commit, replace-exercise. K3.2 adds the load-axis KIND
 *  editor (applyPrescribeAxis, below). The AMRAP command, unit toggle,
 *  per-slot laterality and the two history-seeding actions are still unowned
 *  here — every one of those is a fail-safe NO-OP, never a wrong write.
 *
 *  ⚠️ ADDRESSING IS RESOLVED FROM THE DRAFT BEING MUTATED, never a value
 *  captured at render. entryIdx addresses the card's `draft.entries`, which
 *  DROP inactive-week slots — so the entry order is "slots active at weekIdx".
 *  Deriving `slotIdx` from the live `d` inside `setDraft(d => …)` keeps it
 *  consistent even when two slot-count-changing actions (two remove_exercise)
 *  fire before a re-render — a stale captured index would delete the wrong slot
 *  (K2.2 review, jl-code-standards-auditor High).
 */

/** Only the cursor — the day (session) and the week (cell column). The slot is
 *  resolved from the draft at mutation time, so nothing here can go stale. */
export type PrescribeAddrCtx = {
  sIdx: number;
  weekIdx: number;
};

export type PrescribeAddr = {
  sIdx: number;
  slotIdx: number;
  rowIdx: number;
  weekIdx: number;
};

/** The slot indices active at `weekIdx`, in order — exactly the slots
 *  `slotDraftToEntry` keeps (it returns null on `inactiveWeeks[weekIdx]`), so
 *  the N-th active slot is the card's N-th entry. */
const activeSlotIdxsAt = (
  draft: Draft,
  sIdx: number,
  weekIdx: number,
): number[] => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const out: number[] = [];
  slots.forEach((slot, i) => {
    if (!slot.inactiveWeeks[weekIdx]) out.push(i);
  });
  return out;
};

/** entryIdx + setIdx -> the program-Draft address, resolved against `draft`;
 *  null when the entry has no live slot (a stale index between renders). */
export const resolvePrescribeAddr = (
  draft: Draft,
  ctx: PrescribeAddrCtx,
  entryIdx: number,
  setIdx: number,
): PrescribeAddr | null => {
  const slotIdx = activeSlotIdxsAt(draft, ctx.sIdx, ctx.weekIdx)[entryIdx];
  if (slotIdx === undefined) return null;
  return { sIdx: ctx.sIdx, slotIdx, rowIdx: setIdx, weekIdx: ctx.weekIdx };
};

const memberSlotIdxs = (
  draft: Draft,
  sIdx: number,
  groupId: string,
): number[] => {
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const out: number[] = [];
  slots.forEach((slot, i) => {
    if (slot.group?.id === groupId) out.push(i);
  });
  return out;
};

export const applyLoggerAction = (
  draft: Draft,
  action: LoggerAction,
  ctx: PrescribeAddrCtx,
): Draft => {
  switch (action.type) {
    case "update_set": {
      // ⭐ PE-34/36/42 — this used to be `if (patch.reps === undefined) return
      // draft`, with a comment calling the no-op "fail-safe". It was not: the
      // weight, seconds and AMRAP controls were all VISIBLE and all landed
      // here, so the guard silently ate every edit but reps — the defect it
      // was hiding sat one layer up, in a live input over a write that did not
      // exist [[feedback_a_fail_closed_guard_hides_the_bug_above_it]].
      const addr = resolvePrescribeAddr(draft, ctx, action.entryIdx, action.setIdx);
      if (!addr) return draft;
      const row =
        draft.sessions[addr.sIdx]?.slots[addr.slotIdx]?.setRows[addr.rowIdx];
      const patch: Partial<CellDraft> = {};

      if (action.patch.reps !== undefined) patch.reps = action.patch.reps.trim();

      // PE-34 — the load VALUE. The field speaks the cell's own unit, so the
      // string is stored verbatim; `buildIntensity` validates it at save time
      // exactly as it does for the classic composer.
      // ⛔ Gated on the ROW's axis: history / sforzo / none carry no number,
      // and writing one there would put a value under a kind that ignores it.
      if (
        action.patch.weight !== undefined &&
        row !== undefined &&
        isLoadBearingKind(row.intensityKind)
      )
        patch.intensityValue = action.patch.weight.trim();

      // PE-36 — the seconds of a timed bout. applyPrescribeWorkKind seeds them
      // when "A tempo" goes on; without this they could never be changed, which
      // is what blocks HI-3.
      // ⚠️ Deliberately NOT gated on workKind, unlike the load above: the
      // seconds field only renders on a timed row, and `buildWorkForCell` keys
      // off the ROW's workKind, so a stray value on a reps row is inert and
      // never reaches a payload. Gating the load is different — there the cell
      // value IS read, under whichever kind the row carries.
      if (action.patch.duration !== undefined)
        patch.workDurationSec = action.patch.duration.trim();

      // PE-42 — the AMRAP chip. In prescribe the patch key `amrap` means the
      // coach's REQUEST (there is no outcome on a set nobody has performed);
      // the card reads it back as `amrapPrescribed`. ⛔ `amrapPrescribed` stays
      // out of the action's key union on purpose — keeping it out is what makes
      // "the logger cannot rewrite a plan's request" a type error.
      if (action.patch.amrap !== undefined) patch.amrap = action.patch.amrap;

      if (Object.keys(patch).length === 0) return draft;
      return updateCell(
        draft,
        addr.sIdx,
        addr.slotIdx,
        addr.rowIdx,
        addr.weekIdx,
        patch,
      );
    }
    case "add_set": {
      const addr = resolvePrescribeAddr(draft, ctx, action.entryIdx, 0);
      return addr ? addSetRow(draft, addr.sIdx, addr.slotIdx) : draft;
    }
    case "remove_set": {
      const addr = resolvePrescribeAddr(draft, ctx, action.entryIdx, action.setIdx);
      return addr
        ? removeSetRow(draft, addr.sIdx, addr.slotIdx, addr.rowIdx)
        : draft;
    }
    case "add_round_to_group":
      return memberSlotIdxs(draft, ctx.sIdx, action.groupId).reduce(
        (d, slotIdx) => addSetRow(d, ctx.sIdx, slotIdx),
        draft,
      );
    case "remove_round_from_group":
      return memberSlotIdxs(draft, ctx.sIdx, action.groupId).reduce(
        (d, slotIdx) => removeSetRow(d, ctx.sIdx, slotIdx, action.roundIdx),
        draft,
      );
    case "split_group":
      return memberSlotIdxs(draft, ctx.sIdx, action.groupId).reduce(
        (d, slotIdx) => setSlotGroup(d, ctx.sIdx, slotIdx, null),
        draft,
      );
    case "remove_exercise": {
      const addr = resolvePrescribeAddr(draft, ctx, action.entryIdx, 0);
      return addr ? removeSlot(draft, addr.sIdx, addr.slotIdx) : draft;
    }
    // PE-30 — the picker's commit. `useAddExercise` has always fired these
    // three; until now they fell through to the fail-safe default, so the
    // roster's ＋ opened a picker whose every choice was thrown away. PE-26
    // mounted the picker and PE-29 re-lit the ＋; nothing taught the
    // translator to ADD, and a no-op here is indistinguishable from a broken
    // tap [[feedback_a_half_applied_correction_reasserts_the_error]].
    case "add_exercise": {
      const sIdx = ctx.sIdx;
      if (!draft.sessions[sIdx]) return draft;
      // The new slot lands LAST, which is also where the card shows it: the
      // entries the card lists are "slots active at weekIdx" in slot order,
      // and a fresh slot is active in every week.
      const at = draft.sessions[sIdx].slots.length;
      return updateSlot(addSlot(draft, sIdx), sIdx, at, {
        exerciseId: action.exerciseId,
        exerciseName: action.exerciseName,
      });
    }
    // PE-31 — ⇄ Replace. The button was hidden, not missing: `hideInert`
    // outlived the reason that lit it (PE-26 mounted the picker), and re-lighting
    // it without this would have put back a tap that opens a picker and throws
    // the answer away [[feedback_a_gate_outlives_the_reason_that_lit_it]].
    // ⭐ Mirrors the block editor's own `replaceMember`
    // (useComposerFormMutations.ts:93): the slot swaps IDENTITY and keeps its
    // prescription — rows, kinds, cells, notes. A coach who swaps bench for
    // incline bench means "the same 4×8 @RIR2, on this lift".
    // ⛔ Identity and nothing else. The logger's own payload also carries
    // isBodyweight / isCardio / isTimeBased / movementPattern / loadedSides —
    // facts about how someone TRAINED, which is the same reason
    // `seed_new_entry_sets` stays in the fail-safe default below
    // [[feedback_prescription_never_becomes_measurement]]. `equipmentInstance`
    // is dropped for a different one: a machine is a property of the LOG
    // (equipment-model.md §8.4) and a SlotDraft has nowhere to put it — which
    // is why the picker's machine line is gated OFF in prescribe (LoggerModals'
    // `canTagMachine`), so that drop can never be a silent one.
    case "replace_exercise": {
      const addr = resolvePrescribeAddr(draft, ctx, action.entryIdx, 0);
      return addr
        ? updateSlot(draft, addr.sIdx, addr.slotIdx, {
            exerciseId: action.exerciseId,
            exerciseName: action.exerciseName,
          })
        : draft;
    }
    case "set_group": {
      // ⛔ NOT memberSlotIdxs: these entries do not carry the group yet — that
      // is the write. Each index addresses the CARD's entries, so it resolves
      // the same way every other action here does.
      const group = action.group
        ? {
            id: action.group.id,
            kind: action.group.kind,
            ...(action.group.name !== undefined
              ? { name: action.group.name }
              : {}),
          }
        : null;
      return action.entryIdxs.reduce((d, entryIdx) => {
        const addr = resolvePrescribeAddr(d, ctx, entryIdx, 0);
        return addr ? setSlotGroup(d, addr.sIdx, addr.slotIdx, group) : d;
      }, draft);
    }
    case "align_group_sets":
      return alignGroupSetRows(draft, ctx.sIdx, action.groupId);
    default:
      // Everything else (toggle_unit, set_entry_loaded_sides, cycle_set, fail…)
      // is not K2's to write in prescribe — K3 gates the controls off; until
      // then a no-op is fail-safe (never a silent wrong write).
      //
      // ⭐ `seed_new_entry_sets` and `set_entry_loaded_sides` stay here ON
      // PURPOSE, and they are the two the add path fires right after
      // `add_exercise`: the first prefills a new entry from the athlete's LAST
      // LOGGED session, the second stamps laterality resolved from history.
      // Both are facts about how someone TRAINED; a prescription is what the
      // coach WRITES [[feedback_prescription_never_becomes_measurement]]. A
      // program slot is therefore born blank, not seeded.
      return draft;
  }
};

/** The effort band is RIR-native (useEffortCommit.onSelect passes a canonical
 *  RIR). Prescribing by effort makes effort the SINGLE load axis (mockup: 80%
 *  and RIR 2 cannot coexist), so the row's kind becomes 'rir' and the value
 *  lands on the cell. ⛔ NEVER a DraftSet field (reperto 6). */
export const applyPrescribeEffort = (
  draft: Draft,
  addr: PrescribeAddr | null,
  rir: number,
): Draft => {
  if (!addr) return draft;
  const withKind = setRowKind(draft, addr.sIdx, addr.slotIdx, addr.rowIdx, "rir");
  return updateCell(
    withKind,
    addr.sIdx,
    addr.slotIdx,
    addr.rowIdx,
    addr.weekIdx,
    { intensityValue: String(rir) },
  );
};

/** PE-22 K3.2 — the LoadAxisSelector's write: switches the row's
 *  `intensityKind` to whichever axis the coach picked (kg/%MAX/storico/
 *  sforzo). Sits beside `applyPrescribeEffort` rather than duplicating its
 *  shape: `setRowKind` already seeds/blanks the cell value for the new kind
 *  (VALUELESS_KINDS / seedForIntensityKind), so a bare re-kind is the whole
 *  write — the SFORZO number itself is still set by a tap on the effort
 *  band (applyPrescribeEffort), never by this selector. */
export const applyPrescribeAxis = (
  draft: Draft,
  addr: PrescribeAddr | null,
  kind: IntensityKind,
): Draft => {
  if (!addr) return draft;
  return setRowKind(draft, addr.sIdx, addr.slotIdx, addr.rowIdx, kind);
};

/** PE-22 K3.4 — the "A tempo" chip's write: the reps⇄time toggle, NOT full
 *  HIIT interval authoring (that stays HI-1/HI-3 scope). Turning it ON seeds
 *  `workDurationSec` from the caller (exercise.defaultDurationSec, or a sane
 *  fallback) UNLESS the cell already carries a value — re-toggling on/off/on
 *  must not throw away a number the coach already typed. Turning it OFF
 *  clears the seconds, same defensive-clear the BOUNDS chip already applies
 *  in the block editor (ComposerSetRow.tsx:238-247): a duration left behind
 *  on a set no longer countdown-bound would resurface the moment "A tempo"
 *  is tapped on again, prescribing a bout the coach had removed. */
export const applyPrescribeWorkKind = (
  draft: Draft,
  addr: PrescribeAddr | null,
  timed: boolean,
  seedDurationSec: string,
): Draft => {
  if (!addr) return draft;
  const kind = timed ? "countdown" : undefined;
  const withKind = setWorkKind(draft, addr.sIdx, addr.slotIdx, addr.rowIdx, kind);
  if (!timed) {
    return updateCell(withKind, addr.sIdx, addr.slotIdx, addr.rowIdx, addr.weekIdx, {
      workDurationSec: undefined,
    });
  }
  const existing =
    withKind.sessions[addr.sIdx]?.slots[addr.slotIdx]?.setRows[addr.rowIdx]
      ?.cells[addr.weekIdx]?.workDurationSec ?? "";
  return updateCell(withKind, addr.sIdx, addr.slotIdx, addr.rowIdx, addr.weekIdx, {
    workDurationSec: existing.trim() !== "" ? existing : seedDurationSec,
  });
};

/** PE-37 — the per-EXERCISE note's write: SlotDraft.instructions.
 *
 *  Takes the ADDRESS like its siblings, but uses only the slot half of it: the
 *  note belongs to the exercise, so it is the same text on every week and on
 *  every set row. That asymmetry is the whole difference from the chip below,
 *  and it is why the two are separate writes rather than one with a flag.
 */
export const applyPrescribeEntryNote = (
  draft: Draft,
  addr: PrescribeAddr | null,
  text: string,
): Draft =>
  addr ? updateSlotInstructions(draft, addr.sIdx, addr.slotIdx, text) : draft;

/** PE-22 K3.4 — the "Nota" chip's write: SetRowDraft.notesPerCell[weekIdx].
 *  Thin address-resolving wrapper over the shared `updateSetRowNote`, same
 *  shape as every other apply* here. */
export const applyPrescribeNote = (
  draft: Draft,
  addr: PrescribeAddr | null,
  text: string,
): Draft => {
  if (!addr) return draft;
  return updateSetRowNote(draft, addr.sIdx, addr.slotIdx, addr.rowIdx, addr.weekIdx, text);
};
